import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { z } from "zod";
import { smbPrimaryCtaSchema, smbProofSchema, smbPublicContactSchema } from "@shared/smb-intake";
import { smbHomeOverridesSchema, smbHomePreviewSchema } from "@shared/smb-home-review";
import type { SmbHomeOverrides, SmbHomePreview } from "@shared/smb-home-review";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "wouter";
import { AlertCircle, ArrowUpRight, Home, Loader2, Sparkles } from "lucide-react";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { withLpExpressSurfaceUrl } from "@/lib/lp-express-surface";

type Props = {
  brandId: string;
  eligible: boolean;
  eligibilityError?: boolean;
  onRetryEligibility?: () => void;
  designSystemReady: boolean;
  selectedRunId: string;
  selectedPageId: string;
  selectedPageReady: boolean;
  initiallyExpanded?: boolean;
  onAdmitted: (brandId: string, result: { runId?: string; pageId?: string }) => void;
};

type GenerateResponse = { runId?: string; pageId?: string; pending?: boolean };
type SmbHomeAttempt = { fingerprint: string; id: string; payload: Record<string, unknown> };
type Proof = z.infer<typeof smbProofSchema>;
type Cta = z.infer<typeof smbPrimaryCtaSchema>;
const optional = (value: string) => value.trim() ? value.trim() : undefined;

export function LpExpressSmbHomeGenerator({
  brandId, eligible, eligibilityError = false, onRetryEligibility, designSystemReady,
  selectedRunId, selectedPageId, selectedPageReady, initiallyExpanded = false, onAdmitted,
}: Props) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [ctaType, setCtaType] = useState<Cta["type"]>("phone");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaPhone, setCtaPhone] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactAddress, setContactAddress] = useState("");
  const [contactHours, setContactHours] = useState("");
  const [tone, setTone] = useState("");
  const [claims, setClaims] = useState("");
  const [rating, setRating] = useState("");
  const [reviewCount, setReviewCount] = useState("");
  const [years, setYears] = useState("");
  const [licensed, setLicensed] = useState<"" | "yes" | "no">("");
  const [testimonials, setTestimonials] = useState<Proof["testimonials"]>([]);
  const [clearContact, setClearContact] = useState(false);
  const [clearProof, setClearProof] = useState(false);
  const [clearTone, setClearTone] = useState(false);
  const [clearClaims, setClearClaims] = useState(false);
  const [saveToBrand, setSaveToBrand] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState<{ runId?: string; pageId?: string } | null>(null);
  const [acceptedResolved, setAcceptedResolved] = useState(false);
  const lockRef = useRef(false);
  const attemptRef = useRef<SmbHomeAttempt | null>(null);
  const attemptsByBrandRef = useRef(new Map<string, SmbHomeAttempt>());
  const activeBrandRef = useRef(brandId);
  const brandRef = useRef(brandId);
  brandRef.current = brandId;
  const firstBrandRenderRef = useRef(true);
  const admittedRef = useRef(onAdmitted);
  admittedRef.current = onAdmitted;

  const preview = useQueryPreview(brandId, eligible);

  useEffect(() => {
    if (firstBrandRenderRef.current) {
      firstBrandRenderRef.current = false;
      return;
    }
    const previousBrandId = activeBrandRef.current;
    if (previousBrandId !== brandId) {
      if (attemptRef.current) attemptsByBrandRef.current.set(previousBrandId, attemptRef.current);
      activeBrandRef.current = brandId;
      attemptRef.current = attemptsByBrandRef.current.get(brandId) ?? null;
    }
    setExpanded(false);
    setSelectedTemplate("");
    setSaveToBrand(false);
    setSubmitError("");
    setAccepted(null);
    setAcceptedResolved(false);
    setSubmitError(attemptRef.current
      ? "This brand has an unresolved request. Retry to resume the same saved payload and identity."
      : "");
  }, [brandId]);

  useEffect(() => {
    if (
      accepted &&
      selectedPageReady &&
      ((accepted.runId && accepted.runId === selectedRunId) ||
        (!accepted.runId && accepted.pageId && accepted.pageId === selectedPageId))
    ) setAcceptedResolved(true);
  }, [accepted, selectedRunId, selectedPageId, selectedPageReady]);

  useEffect(() => {
    const facts = preview.data?.facts;
    if (!facts) return;
    setCtaType(facts.primaryCta?.type ?? "phone");
    setCtaLabel(facts.primaryCta?.label ?? "");
    setCtaPhone(facts.primaryCta?.phone ?? "");
    setCtaUrl(facts.primaryCta?.url ?? "");
    setContactPhone(facts.publicContact?.phone ?? "");
    setContactEmail(facts.publicContact?.email ?? "");
    setContactAddress(facts.publicContact?.address ?? "");
    setContactHours(facts.publicContact?.hours ?? "");
    setTone(facts.tone ?? "");
    setClaims(facts.claimsToAvoid ?? "");
    setRating(facts.proofNotes?.googleRating === undefined ? "" : String(facts.proofNotes.googleRating));
    setReviewCount(facts.proofNotes?.reviewCount === undefined ? "" : String(facts.proofNotes.reviewCount));
    setYears(facts.proofNotes?.yearsInBusiness === undefined ? "" : String(facts.proofNotes.yearsInBusiness));
    setLicensed(facts.proofNotes?.licensed === undefined ? "" : facts.proofNotes.licensed ? "yes" : "no");
    setTestimonials(facts.proofNotes?.testimonials ?? []);
  }, [preview.data, brandId]);

  useEffect(() => {
    if (!selectedTemplate && preview.data?.templates.length) {
      setSelectedTemplate(preview.data.templates.find((item) => item.eligible)?.key ?? "");
    }
  }, [preview.data, selectedTemplate]);

  const effectiveCta = useMemo(() => {
    const raw = {
      type: ctaType,
      ...(optional(ctaLabel) ? { label: optional(ctaLabel) } : {}),
      ...(ctaType === "phone"
        ? (optional(ctaPhone) ? { phone: optional(ctaPhone) } : {})
        : (optional(ctaUrl) ? { url: optional(ctaUrl) } : {})),
    };
    return smbPrimaryCtaSchema.safeParse(raw);
  }, [ctaType, ctaLabel, ctaPhone, ctaUrl]);

  const proofValue = useMemo(() => {
    const raw: Record<string, unknown> = {};
    if (optional(rating)) raw.googleRating = Number(rating);
    if (optional(reviewCount)) raw.reviewCount = Number(reviewCount);
    if (optional(years)) raw.yearsInBusiness = Number(years);
    if (licensed) raw.licensed = licensed === "yes";
    if (testimonials?.length) raw.testimonials = testimonials;
    return smbProofSchema.safeParse(raw);
  }, [rating, reviewCount, years, licensed, testimonials]);

  const previewFact = preview.data?.facts ?? {};
  const availableTemplates = preview.data?.templates.filter((template) => template.eligible) ?? [];
  const selected = availableTemplates.find((item) => item.key === selectedTemplate);
  const ctaReady = effectiveCta.success;
  const proofReady = proofValue.success;

  const retryLocked = Boolean(attemptRef.current && (submitError || submitting));
  const formLocked = retryLocked || !!accepted;
  const submit = async () => {
    if (lockRef.current) return;
    if (!selectedTemplate || !selected) {
      setFormError("Choose an available Home page template.");
      return;
    }
    if (!ctaReady) {
      setFormError(effectiveCta.success ? "" : effectiveCta.error.issues[0]?.message ?? "Complete the primary call to action.");
      return;
    }
    if (!proofReady) {
      setFormError(proofValue.success ? "" : `Proof needs review: ${proofValue.error.issues[0]?.message ?? "check the values."}`);
      return;
    }
    if (!designSystemReady) {
      setFormError("Wait for the brand design-system selection to save before generating.");
      return;
    }
    const publicContact = {
      ...(optional(contactPhone) ? { phone: optional(contactPhone) } : {}),
      ...(optional(contactEmail) ? { email: optional(contactEmail) } : {}),
      ...(optional(contactAddress) ? { address: optional(contactAddress) } : {}),
      ...(optional(contactHours) ? { hours: optional(contactHours) } : {}),
    };
    const contactCheck = smbPublicContactSchema.safeParse(publicContact);
    if (!contactCheck.success) {
      setFormError(contactCheck.error.issues[0]?.message ?? "Check public contact details.");
      return;
    }
    const overrides: SmbHomeOverrides = {
      primaryCta: effectiveCta.data,
      publicContact: clearContact ? null : Object.keys(publicContact).length ? contactCheck.data : (previewFact.publicContact ? null : undefined),
      proofNotes: clearProof ? null : proofValue.data && Object.keys(proofValue.data).length ? proofValue.data : (previewFact.proofNotes ? null : undefined),
      tone: clearTone ? null : optional(tone) ?? (previewFact.tone ? null : undefined),
      claimsToAvoid: clearClaims ? null : optional(claims) ?? (previewFact.claimsToAvoid ? null : undefined),
    };
    const overridesCheck = smbHomeOverridesSchema.safeParse(overrides);
    if (!overridesCheck.success) {
      setFormError(overridesCheck.error.issues[0]?.message ?? "Check the business facts before generating.");
      return;
    }
    const payload: Record<string, unknown> = {
      catalogKey: selectedTemplate,
      overrides: overridesCheck.data,
      saveToBrand,
    };
    const fingerprint = JSON.stringify({ brandId, payload });
    if (!attemptRef.current) {
      attemptRef.current = { fingerprint, id: crypto.randomUUID(), payload };
    }
    const attempt = attemptRef.current;
    lockRef.current = true;
    setSubmitting(true);
    setFormError("");
    setSubmitError("");
    try {
      const response = await apiRequest(
        "POST",
        withLpExpressSurfaceUrl(`/api/brands/${brandId}/lp-express/generate-smb-home`, "lp-express-2"),
        { ...attempt.payload, requestIdentity: attempt.id },
      );
      const result = await parseJsonResponse(response) as GenerateResponse;
      if (brandRef.current !== brandId) return;
      if (!result.runId && !result.pageId) throw new Error("The page was accepted without a run or page reference. Reload this brand before trying again.");
      setAccepted({ runId: result.runId, pageId: result.pageId });
      admittedRef.current(brandId, result);
    } catch (error) {
      if (brandRef.current === brandId) {
        const failure = describeSmbHomeFailure(error);
        if (failure.rejectedBeforeAdmission) {
          attemptRef.current = null;
          attemptsByBrandRef.current.delete(brandId);
        }
        setSubmitError(failure.message);
      }
    } finally {
      lockRef.current = false;
      setSubmitting(false);
    }
  };

  if (eligibilityError) {
    return (
      <div className="mt-3 rounded-xl border border-amber-900/15 bg-[#f8f6ee] p-4 text-sm text-teal-950/80" data-testid="smb-home-access-check-error">
        <p>Home page availability could not be checked.</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={onRetryEligibility}>Check again</Button>
      </div>
    );
  }
  if (!eligible) return null;

  return (
    <Card className="mt-3 border-teal-900/15 bg-[#f6faf8] shadow-none" data-testid="smb-home-generator">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-teal-950 text-[#e2efe9]">
            <Home className="h-4 w-4" />
          </span>
          <div>
            <CardTitle className="text-sm font-semibold text-teal-950">SMB Home page</CardTitle>
          <p className="mt-0.5 text-xs text-teal-950/65">Build from saved business facts and supplied photos.</p>
          </div>
        </div>
        <Button size="sm" variant={expanded ? "outline" : "default"} onClick={() => setExpanded((open) => !open)}>
          {expanded ? "Close review" : <><Sparkles className="mr-1.5 h-3.5 w-3.5" />Review & generate</>}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-5 border-t border-teal-900/10 px-4 py-4">
          {preview.isLoading && <div className="space-y-2" aria-label="Loading business facts"><div className="h-3 w-2/3 animate-pulse rounded bg-teal-900/10" /><div className="h-3 w-full animate-pulse rounded bg-teal-900/10" /><div className="h-20 animate-pulse rounded-lg bg-teal-900/10" /></div>}
          {preview.error && <InlineError text="Business facts could not be loaded." retry={preview.refetch} />}
          {preview.data && (
            <>
              <fieldset disabled={formLocked} className="min-w-0 space-y-5 disabled:opacity-75">
              <div className="grid gap-4 md:grid-cols-[1.3fr_.7fr]">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Home page template</Label>
                    {availableTemplates.length ? (
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger data-testid="smb-home-template"><SelectValue placeholder="Choose an available template" /></SelectTrigger>
                        <SelectContent>{availableTemplates.map((template) => <SelectItem value={template.key} key={template.key}>{template.label}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <div className="rounded-lg border border-dashed border-teal-900/20 px-3 py-3 text-sm text-teal-950/70">
                        <p>No Home page templates are available for this brand yet.</p>
                        <Button className="mt-2" type="button" size="sm" variant="outline" onClick={preview.refetch}>Check again</Button>
                      </div>
                    )}
                    {selected?.description && <p className="text-xs text-muted-foreground">{selected.description}</p>}
                  </div>
                  <div className="rounded-xl border border-teal-900/10 bg-white/70 p-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-950/60">Business snapshot</p>
                    <p className="whitespace-pre-line text-sm leading-6 text-teal-950/80">{preview.data.businessInfoPreview || "No business summary is available yet."}</p>
                    {preview.data.missing.length > 0 && <p className="mt-2 text-xs text-amber-800">Missing: {preview.data.missing.join(", ")}</p>}
                    {preview.data.warnings.length > 0 && <p className="mt-2 text-xs text-amber-800">{preview.data.warnings.join(" ")}</p>}
                  </div>
                </div>
                <div className="rounded-xl border border-teal-900/10 bg-white/70 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-950/60">Included as-is</p>
                    <Link className="inline-flex items-center gap-1 text-xs font-medium text-teal-800 underline-offset-2 hover:underline" href={`/brands/${brandId}/settings`}>
                      Edit brand facts <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </div>
                  <p className="mt-3 text-xs font-medium text-teal-950">Services</p>
                  <ul className="mt-1 space-y-1 text-sm text-teal-950/75">{preview.data.services.length ? preview.data.services.map((service) => <li key={service.id}>{service.name}</li>) : <li>No services on file.</li>}</ul>
                  <p className="mt-3 text-xs font-medium text-teal-950">Service areas</p>
                  <p className="mt-1 text-sm text-teal-950/75">{preview.data.serviceAreas.length ? preview.data.serviceAreas.join(", ") : "No service areas on file."}</p>
                  <Link className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-teal-800 underline-offset-2 hover:underline" href={`/brands/${brandId}/settings`}>
                    Update services and areas <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-teal-950">Call to action</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Action type"><Select value={ctaType} onValueChange={(value) => setCtaType(value as Cta["type"])}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="phone">Call by phone</SelectItem><SelectItem value="booking">Book online</SelectItem><SelectItem value="quote_form">Request a quote</SelectItem></SelectContent></Select></Field>
                  <Field label="Button label"><Input value={ctaLabel} onChange={(event) => setCtaLabel(event.target.value)} placeholder="For example, Call today" /></Field>
                  {ctaType === "phone" ? <Field label="CTA phone"><Input value={ctaPhone} onChange={(event) => setCtaPhone(event.target.value)} placeholder="Business phone" /></Field> : <Field label="HTTPS destination"><Input value={ctaUrl} onChange={(event) => setCtaUrl(event.target.value)} placeholder="https://…" /></Field>}
                </div>
                {!ctaReady && <p className="text-xs text-destructive" role="alert">{effectiveCta.success ? "" : effectiveCta.error.issues[0]?.message}</p>}
              </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold text-teal-950">Public contact details</h3><ClearButton active={clearContact} onClick={() => setClearContact((value) => !value)} /></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Phone"><Input value={contactPhone} onChange={(event) => { setContactPhone(event.target.value); setClearContact(false); }} /></Field>
                  <Field label="Public email"><Input value={contactEmail} onChange={(event) => { setContactEmail(event.target.value); setClearContact(false); }} /></Field>
                  <Field label="Address"><Input value={contactAddress} onChange={(event) => { setContactAddress(event.target.value); setClearContact(false); }} /></Field>
                  <Field label="Hours"><Input value={contactHours} onChange={(event) => { setContactHours(event.target.value); setClearContact(false); }} /></Field>
                </div>
              </section>

              <div className="grid gap-4 md:grid-cols-2">
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold text-teal-950">Structured proof</h3><ClearButton active={clearProof} onClick={() => setClearProof((value) => !value)} /></div>
                  <p className="text-xs text-muted-foreground">Only supplied facts are used. Testimonials remain verbatim.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Rating"><Input inputMode="decimal" value={rating} onChange={(event) => { setRating(event.target.value); setClearProof(false); }} placeholder="0 to 5" /></Field>
                    <Field label="Review count"><Input inputMode="numeric" value={reviewCount} onChange={(event) => { setReviewCount(event.target.value); setClearProof(false); }} /></Field>
                    <Field label="Years in business"><Input inputMode="decimal" value={years} onChange={(event) => { setYears(event.target.value); setClearProof(false); }} /></Field>
                    <Field label="Licensed"><Select value={licensed || "unset"} onValueChange={(value) => { setLicensed(value === "unset" ? "" : value as "yes" | "no"); setClearProof(false); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unset">Not stated</SelectItem><SelectItem value="yes">Licensed</SelectItem><SelectItem value="no">Not licensed</SelectItem></SelectContent></Select></Field>
                  </div>
                  {(testimonials ?? []).map((testimonial, index) => (
                    <div className="grid gap-2 rounded-lg bg-white/70 p-2" key={index}>
                      <Textarea aria-label={`Testimonial ${index + 1}`} value={testimonial.quote} onChange={(event) => setTestimonials((rows) => rows?.map((row, rowIndex) => rowIndex === index ? { ...row, quote: event.target.value } : row))} />
                      <div className="flex gap-2"><Input aria-label={`Testimonial attribution ${index + 1}`} value={testimonial.attribution} onChange={(event) => setTestimonials((rows) => rows?.map((row, rowIndex) => rowIndex === index ? { ...row, attribution: event.target.value } : row))} /><Button variant="ghost" size="sm" onClick={() => setTestimonials((rows) => rows?.filter((_, rowIndex) => rowIndex !== index))}>Remove</Button></div>
                    </div>
                  ))}
                  {(testimonials?.length ?? 0) < 6 && <Button type="button" variant="outline" size="sm" onClick={() => setTestimonials((rows) => [...(rows ?? []), { quote: "", attribution: "" }])}>Add testimonial</Button>}
                  {!proofReady && <p className="text-xs text-destructive">{proofValue.success ? "" : proofValue.error.issues[0]?.message}</p>}
                  <p className="rounded-lg bg-teal-900/5 px-3 py-2 text-xs text-teal-950/70">No proof or supplied photos is fine. These are optional and will not block generation.</p>
                </section>
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold text-teal-950">Voice and guardrails</h3></div>
                  <Field label="Tone"><Input value={tone} onChange={(event) => { setTone(event.target.value); setClearTone(false); }} placeholder="Warm, direct, locally trusted" /><ClearButton active={clearTone} onClick={() => setClearTone((value) => !value)} /></Field>
                  <Field label="Claims to avoid"><Textarea value={claims} onChange={(event) => { setClaims(event.target.value); setClearClaims(false); }} placeholder="Anything the page must not claim" /><ClearButton active={clearClaims} onClick={() => setClearClaims((value) => !value)} /></Field>
                  <label className="flex items-start gap-2 rounded-lg border border-teal-900/10 bg-white/70 p-3 text-sm text-teal-950/80">
                    <input type="checkbox" checked={saveToBrand} onChange={(event) => setSaveToBrand(event.target.checked)} className="mt-1 accent-teal-900" />
                    <span><strong className="font-medium">Save these edits to brand facts</strong><span className="mt-0.5 block text-xs text-muted-foreground">Off by default. Only the facts above are saved.</span></span>
                  </label>
                </section>
              </div>
              </fieldset>
              {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
              {submitError && <div role="alert" className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">{submitError} Retry submits the same saved request details and identity.</div>}
              {accepted && (
                <p className="rounded-lg bg-teal-900/5 px-3 py-2 text-sm text-teal-950/80" role="status">
                  {acceptedResolved ? "Your Home page is ready. Open the review to create another page." : "Your page was accepted. Opening the existing editor while its durable generation finishes."}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-teal-900/10 pt-4">
                <p className="max-w-lg text-xs text-muted-foreground">Review the CTA destination carefully. It is the only destination used by the generated page.</p>
                {accepted && acceptedResolved ? (
                  <Button type="button" onClick={() => {
                    attemptRef.current = null;
                    attemptsByBrandRef.current.delete(brandId);
                    setAccepted(null);
                    setAcceptedResolved(false);
                    setSubmitError("");
                    setFormError("");
                    setExpanded(true);
                  }}>Create another Home page</Button>
                ) : (
                  <Button disabled={!!accepted || (!retryLocked && (!availableTemplates.length || !ctaReady || !proofReady || !designSystemReady || !selected)) || submitting} onClick={() => void submit()} data-testid="button-smb-home-generate">
                    {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting page…</> : accepted ? "Page accepted" : "Generate Home page"}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function SmbHomeWholePageRegenerate({
  brandId, pageId, catalogKey, currentCopy, onAdmitted,
}: {
  brandId: string;
  pageId: string;
  catalogKey: string;
  currentCopy: string;
  onAdmitted: (brandId: string, result: { runId?: string; pageId?: string }) => void;
}) {
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const lockRef = useRef(false);
  const identityRef = useRef<{ fingerprint: string; id: string; payload: Record<string, unknown> } | null>(null);
  const brandRef = useRef(brandId);
  brandRef.current = brandId;
  const admittedRef = useRef(onAdmitted);
  admittedRef.current = onAdmitted;
  useEffect(() => {
    setFeedback("");
    setError("");
    setAccepted(false);
    identityRef.current = null;
  }, [brandId, pageId]);

  const submit = async () => {
    if (lockRef.current || !feedback.trim()) return;
    const payload: Record<string, unknown> = {
      catalogKey,
      previousPageId: pageId,
      feedback: feedback.trim(),
      currentCopy,
    };
    const fingerprint = JSON.stringify({ brandId, payload });
    if (!identityRef.current) {
      identityRef.current = { fingerprint, id: crypto.randomUUID(), payload };
    }
    const attempt = identityRef.current;
    lockRef.current = true;
    setPending(true);
    setError("");
    try {
      const response = await apiRequest(
        "POST",
        withLpExpressSurfaceUrl(`/api/brands/${brandId}/lp-express/generate-smb-home`, "lp-express-2"),
        { ...attempt.payload, requestIdentity: attempt.id },
      );
      const result = await parseJsonResponse(response) as GenerateResponse;
      if (brandRef.current !== brandId) return;
      if (!result.runId && !result.pageId) throw new Error("The new page was accepted without a run reference. Reload this brand before trying again.");
      setAccepted(true);
      admittedRef.current(brandId, result);
      setFeedback("");
    } catch (cause) {
      if (brandRef.current === brandId) {
        const failure = describeSmbHomeFailure(cause);
        if (failure.rejectedBeforeAdmission) identityRef.current = null;
        setError(failure.message);
      }
    } finally {
      lockRef.current = false;
      setPending(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-teal-900/15 bg-[#f6faf8] p-4" data-testid="smb-home-whole-page-regenerate">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-teal-950"><Sparkles className="h-4 w-4" />Regenerate whole page with notes</div>
      <p className="mb-3 text-xs leading-5 text-teal-950/70">Creates a new retained page and leaves this version unchanged. This may incur paid Studio generation charges.</p>
      <Textarea disabled={Boolean((identityRef.current && error) || accepted || pending)} value={feedback} onChange={(event) => setFeedback(event.target.value)} placeholder="What should change across the full page?" maxLength={4000} />
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error} Retry submits the same saved notes and identity.</p>}
      {accepted && <p className="mt-2 text-sm text-teal-950/75" role="status">The new page was accepted. This action stays locked until its owning page changes.</p>}
      <Button className="mt-3" variant="outline" disabled={accepted || ((!identityRef.current && !feedback.trim()) || pending)} onClick={() => void submit()} data-testid="button-smb-home-regenerate">
        {pending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting new page…</> : accepted ? "New page accepted" : "Regenerate whole page"}
      </Button>
    </div>
  );
}

function useQueryPreview(brandId: string, enabled: boolean) {
  const [data, setData] = useState<SmbHomePreview | undefined>();
  const [state, setState] = useState({ loading: false, error: false });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    if (!enabled) { setData(undefined); setState({ loading: false, error: false }); return; }
    setData(undefined);
    setState({ loading: true, error: false });
    fetch(`/api/brands/${brandId}/lp-express/smb-home-preview-info?surface=lp-express-2`, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Preview load failed");
        return smbHomePreviewSchema.parse(await response.json()) as SmbHomePreview;
      })
      .then((value) => { if (active) { setData(value); setState({ loading: false, error: false }); } })
      .catch(() => { if (active) setState({ loading: false, error: true }); });
    return () => { active = false; };
  }, [brandId, enabled, version]);
  return { data, isLoading: state.loading, error: state.error, refetch: () => setVersion((value) => value + 1) };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}

function ClearButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={onClick}>{active ? "Will clear" : "Clear saved value"}</Button>;
}

function InlineError({ text, retry }: { text: string; retry: () => void }) {
  return <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{text}</span><Button variant="outline" size="sm" onClick={retry}>Retry</Button></div>;
}

function describeSmbHomeFailure(error: unknown): { message: string; rejectedBeforeAdmission: boolean } {
  const message = error instanceof Error ? error.message : "";
  const match = message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const status = match ? Number(match[1]) : 0;
  const body = match?.[2];
  if (body) {
    try {
      const parsed = JSON.parse(body) as {
        code?: unknown;
        message?: unknown;
        admission?: { runId?: unknown; pageId?: unknown };
      };
      const safeMessage = typeof parsed.message === "string" ? parsed.message : "";
      const code = typeof parsed.code === "string" ? parsed.code : "";
      if (
        parsed.code === "smb_home_save_failed" &&
        parsed.admission &&
        (typeof parsed.admission.runId === "string" || typeof parsed.admission.pageId === "string")
      ) {
        const reference = typeof parsed.admission.runId === "string"
          ? `run ${parsed.admission.runId}`
          : `page ${parsed.admission.pageId}`;
        return {
          message: `The page was accepted as ${reference}, but the explicit business-fact save did not finish (${code}). Retry unchanged details to resume this request.`,
          rejectedBeforeAdmission: false,
        };
      }
      if (safeMessage) {
        return {
          message: code ? `${safeMessage} (${code})` : safeMessage,
          rejectedBeforeAdmission: status >= 400 && status < 500 && !parsed.admission,
        };
      }
      return {
        message: "The server could not confirm this page request. Retry the same details to safely resume it.",
        rejectedBeforeAdmission: status >= 400 && status < 500,
      };
    } catch {
      return {
        message: "The server could not confirm this page request. Retry the same details to safely resume it.",
        rejectedBeforeAdmission: status >= 400 && status < 500,
      };
    }
  }
  if (!message || message.includes("<") || message.length > 300) {
    return {
      message: "The server could not confirm this page request. Retry the same details to safely resume it.",
      rejectedBeforeAdmission: false,
    };
  }
  return { message, rejectedBeforeAdmission: false };
}
